/**
 * 랭킹 — 두 검사 항목이다: **레벨순 · 전투력순** (POWER-R1).
 *
 * > 사용자: *"NPC와 유저 모두 전투력을 평가해서 랭킹에 반영해줘. 단순 레벨 말고."*
 *
 * ⚠ **검사 항목을 바꾸면 표를 서버에서 다시 받는다.** 받은 20명을 화면에서 재정렬하면
 * 그 20명은 레벨 상위일 뿐이라 **전투력 상위가 표 밖에 남는다.**
 * 떠돌이도 같은 표에 선다 — 서버가 같은 산식(`powerOf`)으로 재서 합쳐 보낸다.
 * R156(레드팀 C17) — 원주민 행에는 「원주민」 태그를 단다(서버 `/api/rank` 가 원주민 행에 native 를 붙인다). 사람과 구분이 안 돼
 * 「봇으로 인구를 부풀린다」로 읽혔다.
 * 관리자는 서버가 이미 뺀다(14.42).
 */
import { S, esc } from '../../core/state.js';
import { fold, lazyChunks, muted, num, sec } from './common.js';
import { levelLabel } from '../../core/level-label.js';
import { starMark } from '../star.js';
import { rankBadgeChip } from '../rank-badge.js';

export function render() {
  /**
   * 내 처치·사망은 **여기가 집이다.**
   * 전에는 HUD 하단에 상시로 떠 있었는데, 사냥 중에 볼 값이 아니라
   * 궁금할 때 여는 값이다. 상시 노출은 은화 한 줄만 남긴다(C-29).
   */
  const y = S.you;
  let h = (y ? sec('내 기록',
    `<div class="ux-facts rank-summary">`
    // POWER-R1 — 내 전투력. 값은 서버가 준다(옛 서버면 칸을 안 그린다)
    + (Number.isFinite(y.power) ? `<div class="ux-fact ux-primary"><small>내 전투력</small><b class="ux-number">${num(y.power)}</b></div>` : '')
    + `<div class="ux-fact"><small>처치</small><b class="ux-number">${y.kills == null ? '확인 중' : num(y.kills)}</b><small>회</small></div>`
    + `<div class="ux-fact"><small>사망</small><b class="ux-number">${y.deaths == null ? '확인 중' : num(y.deaths)}</b><small>회</small></div>`
    // UNI-Q193 — 이번 세계 PK 처치(명예의 전당 PK왕이 세는 수). 서버가 몸 가방에 센 값 그대로
    + `<div class="ux-fact"><small>PK 처치 (이번 세계)</small><b class="ux-number">${y.inv?.pkKills == null ? '확인 중' : num(y.inv.pkKills)}</b><small>회</small></div></div>`) : '');

  // Q276 — 시련의 탑은 [행사] › 시련의 탑, 명예의 전당은 메뉴 › 세계 › 명예의 전당 한 곳으로 옮겼다(전에는 이 창 안의 버튼이었다)

  /**
   * UNI-Q161 — 초당 피해는 **다른 검사 항목**이라 표를 따로 둔다(전체·직업별·내 순위 셋을
   * 한 표에 얹으면 레벨/전투력 표가 읽히지 않는다). 명예의 전당과 같은 결로 문 하나만 둔다.
   */
  const dps = sec('초당 피해',
    '<button onclick="act.dpsPanel()">초당 피해 순위 보기</button>'
    + muted('마을 광장의 <b>샌드백</b>에서 측정 · 이번 세계·역대 기록'));

  const byPower = S.rankBy === 'power';
  h += sec(`랭킹 <em>${byPower ? '전투력순' : '레벨순'}</em>`,
    `<div class="row">`
    + `<button class="${byPower ? 'ghost' : 'sel'}" onclick="act.rankBy('level')">레벨</button>`
    + `<button class="${byPower ? 'sel' : 'ghost'}" onclick="act.rankBy('power')">전투력</button>`
    + '</div>'
    + (byPower
      /**
       * 산식을 화면에 명시한다(사용자 지시). ⚠ 숫자 20 은 서버 `POWER_FIGHT_SECS` 의
       * 사본이 아니라 **설명 문장**이다 — 값이 바뀌면 이 문장도 같이 고친다
       * (`check-client` 폐기 용어 검사 항목이 아닌, 사람이 읽는 안내다).
       * Q276 — 산식 전문은 [자세히] 안에 둔다.
       */
      ? fold('전투력 = 20초 동안 주는 피해 + 버티는 피해',
        `전투력 = <b>20초 교전 기준</b> 내가 내는 피해(공격×치명 기대÷간격×20) + 버텨내는 피해량((HP+방어×20)÷(1−회피)).
        치명 기대 = 1 + 치명 확률×(치명 배율−1) — 95%를 넘친 민첩은 치명 배율로 센다.
        레벨·스탯·장비·스킬·패시브를 반영한 비교용 수치다. 원주민도 같은 기준으로 계산한다.`)
      : muted('사람과 원주민의 현재 레벨을 비교한다.')));
  if (!S.rank) return h + muted(S.loadErr ? S.loadErr + ' — 패널을 다시 열면 재시도한다.' : '불러오는 중…') + dps;
  return h + lazyChunks(S.rank.map((r, i) => {
    const medal = i === 0 ? '#d4a83c' : i === 1 ? '#b9bcc2' : i === 2 ? '#b07d4a' : 'inherit';
    const me = S.you && r.name === S.you.name;
    // 전투력 0 은 "이 기능 전 마지막 저장" 이다 — 값처럼 보이면 안 되니 —로 그린다
    const tail = byPower
      ? (r.power > 0 ? `<b class="ux-number">${num(r.power)}</b>` : '<span class="qt">—</span>')
      : `<small>Lv.</small><b class="ux-number">${levelLabel(r.level)}</b>`;
    return `<div class="item rank-entry ${me ? 'sel' : ''}">
      <span class="qt rank-place" style="width:26px;text-align:center;color:${medal}">${i + 1}</span>
      <span class="nm">${starMark(r.name)}${esc(r.name)}${r.native ? ' <span class="nativetag">원주민</span>' : ''}${r.rb ? ' ' + rankBadgeChip(r.rb) : ''} <span class="qt">${r.klass === 'ranged' ? '원거리' : r.klass === 'assassin' ? '암살자' : '근접'}${byPower ? ` · Lv.${levelLabel(r.level)}` : ''}</span></span>
      <span class="rank-value">${tail}</span></div>`;
  }), 25) + dps;   // UNI-Q110 ⑦ — 25줄씩 지연 렌더
}
